import { FileQuestion, Search, AlertCircle, Inbox, Users, Truck, MapPin, CreditCard, MessageSquare, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from './button';

const iconMap = {
  default: Inbox,
  search: Search,
  error: AlertCircle,
  question: FileQuestion,
  users: Users,
  drivers: Truck,
  bookings: MapPin,
  payments: CreditCard,
  support: MessageSquare,
  reports: FileText,
};

interface EmptyStateProps {
  icon?: keyof typeof iconMap;
  title: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  className?: string;
}

export function EmptyState({
  icon = 'default',
  title,
  description,
  action,
  className,
}: EmptyStateProps) {
  const Icon = iconMap[icon];

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center py-12 text-center',
        className
      )}
    >
      <div className="rounded-full bg-muted p-4 mb-4">
        <Icon className="h-8 w-8 text-muted-foreground" />
      </div>
      <h3 className="text-lg font-semibold">{title}</h3>
      {description && (
        <p className="mt-1 text-sm text-muted-foreground max-w-sm">
          {description}
        </p>
      )}
      {action && (
        <Button onClick={action.onClick} className="mt-4">
          {action.label}
        </Button>
      )}
    </div>
  );
}

// Preset empty states
export function NoResultsFound({ onReset }: { onReset?: () => void }) {
  return (
    <EmptyState
      icon="search"
      title="No results found"
      description="Try adjusting your search or filter criteria to find what you're looking for."
      action={onReset ? { label: 'Clear filters', onClick: onReset } : undefined}
    />
  );
}

export function NoDataYet({
  entity,
  onAdd,
}: {
  entity: string;
  onAdd?: () => void;
}) {
  return (
    <EmptyState
      icon="default"
      title={`No ${entity} yet`}
      description={`${entity.charAt(0).toUpperCase() + entity.slice(1)} will appear here once they are added.`}
      action={onAdd ? { label: `Add ${entity}`, onClick: onAdd } : undefined}
    />
  );
}

export function ErrorState({
  message = 'Something went wrong',
  onRetry,
}: {
  message?: string;
  onRetry?: () => void;
}) {
  return (
    <EmptyState
      icon="error"
      title="Oops!"
      description={message}
      action={onRetry ? { label: 'Try again', onClick: onRetry } : undefined}
    />
  );
}
